/**
 * SOLOMON AMBIENT FIELD — v3
 * Full-screen quantum particle field that flows across the entire UI.
 *
 * Two modes:
 * - Hero (dimmed=false): Full presence — 280 particles, warm tones, visible connections
 * - Chat (dimmed=true):  Subtle depth — same field but 30-40% dimmer so chat content reads clearly
 *
 * Particles use warm Hermès palette: orange (3-8%), amber (2-5%), gold (1-3%)
 * Connection lines between nearby particles create neural network effect at low opacity (2-4%)
 * Bokeh circles add depth behind everything.
 *
 * The glassmorphism UI panels float over this field — backdrop-filter blur
 * creates the frosted-glass-over-living-particles effect.
 */

import { useRef, useEffect, useCallback } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion.js';
import './ambient.css';

// ─── Tunables ────────────────────────────────────────────────
const NODE_COUNT        = 280;    // 200–400 visible at any time

const CONN_DIST_MIN     = 100;    // px — connection radius range
const CONN_DIST_MAX     = 260;    // px

const IDLE_SPEED        = 0.15;   // slow, organic drift
const SPEAK_SPEED_BOOST = 3.2;
const THINK_SPEED_BOOST = 1.5;

const IDLE_NODE_ALPHA   = 0.06;   // very subtle at rest (3-8%)
const SPEAK_NODE_ALPHA  = 0.55;
const THINK_NODE_ALPHA  = 0.30;

const IDLE_LINE_ALPHA   = 0.03;   // connection lines at 2-4%
const SPEAK_LINE_ALPHA  = 0.28;
const THINK_LINE_ALPHA  = 0.10;

const IDLE_NODE_R       = 1.2;    // 1-3px particle size
const SPEAK_NODE_R      = 2.8;
const THINK_NODE_R      = 1.8;

// Dimmed mode multiplier (for chat background)
const DIM_FACTOR        = 0.55;   // 30-40% dimmer

// ─── Bokeh — warm out-of-focus circles (glassmorphism depth layer)
const BOKEH_COUNT = 10;

function createBokeh(W, H) {
  return Array.from({ length: BOKEH_COUNT }, () => ({
    x: Math.random() * W,
    y: Math.random() * H,
    r: 50 + Math.random() * 140,
    isOrange: Math.random() < 0.55,
    alpha: 0.03 + Math.random() * 0.06,
    phase: Math.random() * Math.PI * 2,
    driftX: (Math.random() - 0.5) * 0.06,
    driftY: (Math.random() - 0.5) * 0.04,
  }));
}

// ─── Color palettes ──────────────────────────────────────────
// Dark mode: warm pastels that glow against near-black bg
const COLORS = {
  orange:    [255, 107,   0],   // hermes-orange
  gold:      [201, 168,  76],   // gold
  warmWhite: [255, 235, 180],
  amber:     [212, 135,  10],   // hermes-amber
};

// Light mode: material palette aligned with orb's carrot-highlight family.
// Particles read as quieter extension of orb atmosphere, not a competing
// lattice. Earlier v2 values (#8B3A0E/#A94A1A/#6E3010) were too dark and
// too assertive — nodes separated from orb rather than unified with it.
// Same keys as COLORS so swap is O(1) at render time via colorKey lookup.
const LIGHT_COLORS = {
  orange:    [180,  84, 32],   // #B45420 — matches orb carrot highlight (45% dots)
  amber:     [201, 106, 58],   // #C96A3A — warmer carrot, slightly brighter (27%)
  gold:      [110,  48, 18],   // #6E3010 — touch of orb's deep core for gravity (16%)
  warmWhite: [180,  84, 32],   // #B45420 — reinforce carrot presence (12%)
};

// Light mode gets its own alpha ladder rather than a multiplier boost.
// A flat visibilityBoost clamped speak/think states to 1.0 and flattened
// the state differential — dedicated targets preserve animation range.
// Values dialed back ~40-50% from over-corrected v2 (0.35/0.85/0.60 →
// 0.22/0.55/0.40): still visible as intentional texture, not aggressive.
const LIGHT_ALPHAS = {
  idleNode:  0.22, speakNode: 0.55, thinkNode: 0.40,
  idleLine:  0.15, speakLine: 0.40, thinkLine: 0.25,
};

const COLOR_KEYS = ['orange', 'amber', 'gold', 'warmWhite'];

function randomNodeColorKey() {
  const r = Math.random();
  if (r < 0.45) return 'orange';
  if (r < 0.72) return 'amber';
  if (r < 0.88) return 'gold';
  return 'warmWhite';
}

function isLightTheme() {
  return typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'light';
}

// Random wave config — called each time speaking starts
function randomWaveConfig(W, H, nodes) {
  const fromNode   = Math.random() < 0.4 && nodes.length > 0;
  const src        = fromNode
    ? nodes[Math.floor(Math.random() * nodes.length)]
    : { x: W / 2, y: H / 2 };
  const speed      = 300 + Math.random() * 320;
  const decay      = 1.4 + Math.random() * 1.4;
  const rings      = 1 + Math.floor(Math.random() * 3);
  const ringGap    = 40 + Math.random() * 60;
  const palette    = isLightTheme() ? LIGHT_COLORS : COLORS;
  const color      = Math.random() < 0.6 ? palette.orange : palette.gold;

  return { cx: src.x, cy: src.y, speed, decay, rings, ringGap, color,
           birth: performance.now() / 1000 };
}

// ─── Node factory ────────────────────────────────────────────
function createNode(W, H) {
  const angle     = Math.random() * Math.PI * 2;
  const baseSpeed = IDLE_SPEED * (0.4 + Math.random() * 1.2);
  const connDist  = CONN_DIST_MIN + Math.random() * (CONN_DIST_MAX - CONN_DIST_MIN);
  return {
    x:       Math.random() * W,
    y:       Math.random() * H,
    vx:      Math.cos(angle) * baseSpeed,
    vy:      Math.sin(angle) * baseSpeed,
    baseVx:  Math.cos(angle) * baseSpeed,
    baseVy:  Math.sin(angle) * baseSpeed,
    r:       IDLE_NODE_R,
    colorKey: randomNodeColorKey(),
    phase:   Math.random() * Math.PI * 2,
    connDist,
    isHub:   connDist > (CONN_DIST_MAX * 0.75),
  };
}

// ─── Main component ──────────────────────────────────────────
export default function AmbientField({ isSpeaking = false, isThinking = false, dimmed = false }) {
  const reduced = usePrefersReducedMotion();
  const canvasRef = useRef(null);
  const stateRef  = useRef({ isSpeaking: false, isThinking: false, dimmed: false });
  const nodesRef  = useRef([]);
  const wavesRef  = useRef([]);
  const bokehRef  = useRef([]);
  const rafRef    = useRef(null);
  const lastRef   = useRef(0);
  const alphaRef  = useRef({
    nodeAlpha: IDLE_NODE_ALPHA,
    lineAlpha: IDLE_LINE_ALPHA,
    nodeR:     IDLE_NODE_R,
    speedMult: 1.0,
    dimMult:   1.0,
  });

  useEffect(() => {
    const wasSpeak = stateRef.current.isSpeaking;
    stateRef.current = { isSpeaking, isThinking, dimmed };

    if (isSpeaking && !wasSpeak && canvasRef.current) {
      const { width: W, height: H } = canvasRef.current;
      const nodes = nodesRef.current;

      const waveCount = 1 + (Math.random() < 0.45 ? 1 : 0);
      for (let w = 0; w < waveCount; w++) {
        wavesRef.current.push(randomWaveConfig(W, H, nodes));
        if (w === 0) continue;
        wavesRef.current[wavesRef.current.length - 1].birth += 0.35;
      }
    }
  }, [isSpeaking, isThinking, dimmed]);

  const initNodes = useCallback((W, H) => {
    nodesRef.current = Array.from({ length: NODE_COUNT }, () => createNode(W, H));
    bokehRef.current = createBokeh(W, H);
  }, []);

  const tick = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const W   = canvas.width;
    const H   = canvas.height;
    const now = timestamp / 1000;
    const dt  = Math.min(now - lastRef.current, 0.05);
    lastRef.current = now;

    const { isSpeaking: speak, isThinking: think, dimmed: dim } = stateRef.current;
    const a   = alphaRef.current;
    const lk  = 0.055;

    // Theme-aware rendering: in light mode, skip the chat DIM_FACTOR (nodes
    // are already hard to see on cream) and swap to material palette + its
    // own alpha ladder (not a multiplier — preserves state differential).
    const light   = isLightTheme();
    const palette = light ? LIGHT_COLORS : COLORS;

    // ── Smooth lerp of display values ──
    const targetNA = light
      ? (speak ? LIGHT_ALPHAS.speakNode : think ? LIGHT_ALPHAS.thinkNode : LIGHT_ALPHAS.idleNode)
      : (speak ? SPEAK_NODE_ALPHA : think ? THINK_NODE_ALPHA : IDLE_NODE_ALPHA);
    const targetLA = light
      ? (speak ? LIGHT_ALPHAS.speakLine : think ? LIGHT_ALPHAS.thinkLine : LIGHT_ALPHAS.idleLine)
      : (speak ? SPEAK_LINE_ALPHA : think ? THINK_LINE_ALPHA : IDLE_LINE_ALPHA);
    const targetR  = speak ? SPEAK_NODE_R     : think ? THINK_NODE_R     : IDLE_NODE_R;
    const targetSM = speak ? SPEAK_SPEED_BOOST : think ? THINK_SPEED_BOOST : 1.0;
    const targetDM = (dim && !light) ? DIM_FACTOR : 1.0;

    a.nodeAlpha += (targetNA - a.nodeAlpha) * lk;
    a.lineAlpha += (targetLA - a.lineAlpha) * lk;
    a.nodeR     += (targetR  - a.nodeR)     * lk;
    a.speedMult += (targetSM - a.speedMult) * lk;
    a.dimMult   += (targetDM - a.dimMult)   * lk;

    const dm = a.dimMult;

    ctx.clearRect(0, 0, W, H);

    // ── Draw warm bokeh background ──
    for (const b of bokehRef.current) {
      b.x += b.driftX;
      b.y += b.driftY;
      if (b.x < -b.r) b.x = W + b.r;
      if (b.x > W + b.r) b.x = -b.r;
      if (b.y < -b.r) b.y = H + b.r;
      if (b.y > H + b.r) b.y = -b.r;

      const breathe = 0.85 + 0.15 * Math.sin(now * 0.4 + b.phase);
      const [cr, cg, cb] = b.isOrange ? palette.orange : palette.amber;
      const grad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
      grad.addColorStop(0, `rgba(${cr},${cg},${cb},${b.alpha * breathe * dm})`);
      grad.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // ── Update node positions ──
    const nodes = nodesRef.current;
    for (const n of nodes) {
      const breathe  = 1.0 + 0.28 * Math.sin(now * 1.1 + n.phase);
      const hubFactor = n.isHub ? 0.65 : 1.0;
      const spd      = a.speedMult * breathe * hubFactor;

      n.x += n.vx * spd * dt * 60;
      n.y += n.vy * spd * dt * 60;

      const m = 60;
      if (n.x < -m)    n.x = W + m;
      if (n.x > W + m) n.x = -m;
      if (n.y < -m)    n.y = H + m;
      if (n.y > H + m) n.y = -m;
    }

    // ── Draw connection lines ──
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx   = nodes[i].x - nodes[j].x;
        const dy   = nodes[i].y - nodes[j].y;
        const dist2 = dx * dx + dy * dy;

        const thresh = (nodes[i].connDist + nodes[j].connDist) * 0.5;
        if (dist2 >= thresh * thresh) continue;

        const dist   = Math.sqrt(dist2);
        const fade   = 1 - dist / thresh;
        const hubBoost = (nodes[i].isHub || nodes[j].isHub) ? 1.35 : 1.0;
        const shimmer  = speak
          ? 0.55 + 0.45 * Math.sin(now * 7.0 + i * 0.5 + j * 0.8)
          : 1.0;
        const lineA = a.lineAlpha * fade * fade * hubBoost * shimmer * dm;

        const [r, g, b] = palette[nodes[i].colorKey];
        ctx.beginPath();
        ctx.moveTo(nodes[i].x, nodes[i].y);
        ctx.lineTo(nodes[j].x, nodes[j].y);
        ctx.strokeStyle = `rgba(${r},${g},${b},${Math.min(lineA, 0.50)})`;
        ctx.lineWidth   = nodes[i].isHub || nodes[j].isHub ? 0.8 : 0.5;
        ctx.stroke();
      }
    }

    // ── Draw radial pulse waves ──
    wavesRef.current = wavesRef.current.filter(wave => {
      const age      = now - wave.birth;
      if (age < 0)   return true;
      if (age > wave.decay) return false;

      const progress = age / wave.decay;
      const baseR    = age * wave.speed;
      const waveA    = (1 - progress) * 0.40 * dm;
      const [r, g, b] = wave.color;

      for (let ring = 0; ring < wave.rings; ring++) {
        const ringR = baseR - ring * wave.ringGap;
        if (ringR <= 0) continue;

        const ringFade = Math.max(0, 1 - ring * 0.32);
        ctx.beginPath();
        ctx.arc(wave.cx, wave.cy, ringR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${r},${g},${b},${waveA * ringFade})`;
        ctx.lineWidth   = ring === 0 ? 1.2 : 0.6;
        ctx.stroke();
      }
      return true;
    });

    // ── Draw nodes ──
    for (const n of nodes) {
      const [r, g, b] = palette[n.colorKey];
      const pulse     = speak
        ? 1.0 + 0.40 * Math.sin(now * 5.8 + n.phase)
        : think
          ? 1.0 + 0.18 * Math.sin(now * 2.4 + n.phase)
          : 1.0;

      const radius    = a.nodeR * pulse * (n.isHub ? 1.3 : 1.0);

      // Outer glow
      ctx.beginPath();
      ctx.arc(n.x, n.y, radius * 2.8, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r},${g},${b},${a.nodeAlpha * 0.15 * pulse * dm})`;
      ctx.fill();

      // Core dot
      ctx.beginPath();
      ctx.arc(n.x, n.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r},${g},${b},${a.nodeAlpha * dm})`;
      ctx.fill();
    }

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (reduced) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      initNodes(canvas.width, canvas.height);
    };

    resize();
    window.addEventListener('resize', resize);
    lastRef.current = performance.now() / 1000;
    rafRef.current  = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, [tick, initNodes, reduced]);

  // Reduced motion (2026-09-02): a continuously animating full-screen field is
  // what the OS setting exists to stop. Nothing renders; the CSS ambient pools
  // carry the atmosphere. A still, seeded frame is the follow-on (ui-declunk goal file, item 8).
  if (reduced) return null;

  return (
    <canvas
      ref={canvasRef}
      className="ambient-field"
      aria-hidden="true"
    />
  );
}
